import React from 'react';
import PropTypes from 'prop-types';
import {Transition} from 'react-transition-group';
import DatasetModel from '../../../models/pipeline/steps/dataset';
import DATASOURCES from '../../../services/datasources';
import {FEATURE_FLAGS, onFlagChange, withFeatureFlag} from '../../../services/feature_flags';
import {globalClickEvent, globalMenuOpenEvent} from '../../../services/global_events';
import {
    aggregationSelectEvent,
    columnSelectEvent,
    filterOperatorSelectEvent,
    filterValueSelectEvent,
    inputBlurEvent,
    inputFocusEvent,
    nextMenuEvent,
} from '../../../services/query_builder_events';
import {queryBuilderState} from '../../../services/query_builder_state';
import {
    getDefaultResultsTableStyles,
    getResultsTableStyles,
} from '../../../utils/pipeline_utils';
import {
    ATTRIBUTES,
    createDatasetAttribute,
    MENUS,
    QUERY_BUILDER_TYPES,
} from '../../../utils/query_utils';
import BasePipelineStepComponent from '../pipeline_steps/base_pipeline_step';
import QueryBuilderHybrid from './query_builder/hybrid';
import QueryBuilderSingleInput from './query_builder/single_input';
import QueryBuilderColumnFirst from './query_builder/column_first';
import ColumnFirstQueryResult from './query_result/column_first';
import SingleInputQueryResult from './query_result/single_input';
import SourceSelector from './source_selector';
import ColumnSelector from './menus/column_selector';
import AggregationSelector from './menus/aggregation_selector';
import FilterOperatorSelector from './menus/filter_operator_selector';
import FilterValuesSelector from './menus/filter_values_selector';
import FilterLogicalSelector from './menus/filter_logical_operator_selector';
import ColumnName from '../../common/column_name';
import TableMenu from "../pipeline_steps/table_actions/menu";

class DatasetPanel extends BasePipelineStepComponent {
    constructor(props) {
        super(props);
        Object.assign(this.state, {
            datasource: DATASOURCES[props.step.datasource],
            queryBuilderType: FEATURE_FLAGS.QUERY_BUILDER_TYPE,
            hasExecuted: false,
        });

        this.onClick = this.onClick.bind(this);
        this.executeQuery = this.executeQuery.bind(this);
        this.setFilterOperator = this.setFilterOperator.bind(this);
        this.setFilterValue = this.setFilterValue.bind(this);
        this.setFilterLogicalOperator = this.setFilterLogicalOperator.bind(this);
        this.hideMenus = this.hideMenus.bind(this);
    }

    componentDidMount() {
        super.componentDidMount();

        this.unregisterGlobalEvent = globalClickEvent.listen(this.hideMenus);
        this.unregisterGlobalMenuEvent = globalMenuOpenEvent.listen((menu) => {
            if (menu !== MENUS.COMBINED_AGG) {
                this.hideMenus();
            }
        });
        this.unbindMeasuresOnlyFeatureFlag = onFlagChange('HIDE_DIMS_AND_FILTERS', () => { this.forceUpdate(); });
        this.unbindQueryBuilderFeatureFlagListener = onFlagChange('QUERY_BUILDER_TYPE', (queryBuilderType) => {
            this.setState({queryBuilderType})
        });
        this.unbindChartioDatasourceListener = onFlagChange('SHOW_CHARTIO_SOURCE', () => { setTimeout(() => { this.forceUpdate(); }, 30); });
        this.unbindInputFocusHandler = inputFocusEvent.listen(() => { this.forceUpdate(); });
        this.unbindInputBlurHandler = inputBlurEvent.listen(() => { this.forceUpdate(); });
        this.unbindColumnSelectHandler = columnSelectEvent.listen(this.handleColumnSelect.bind(this));
        this.unbindAggregationSelectHandler = aggregationSelectEvent.listen(this.handleAggregationSelect.bind(this));
        this.unbindFilterOperatorSelectHandler = filterOperatorSelectEvent.listen(this.setFilterOperator);
        this.unbindFilterValueSelectHandler = filterValueSelectEvent.listen(this.setFilterValue);
    }

    componentDidUpdate(prevProps, prevState) {
        super.componentDidUpdate(prevProps, prevState);

        const {step} = this.props;
        if (prevProps.step !== step) {
            this.setState({datasource: DATASOURCES[step.datasource]});
        }
    }

    componentWillUnmount() {
        super.componentWillUnmount();

        this.unregisterGlobalEvent();
        this.unregisterGlobalMenuEvent();
        this.unbindMeasuresOnlyFeatureFlag();
        this.unbindQueryBuilderFeatureFlagListener();
        this.unbindChartioDatasourceListener();
        this.unbindInputFocusHandler();
        this.unbindColumnSelectHandler();
        this.unbindAggregationSelectHandler();
        this.unbindFilterOperatorSelectHandler();
        this.unbindFilterValueSelectHandler();
        this.unbindInputBlurHandler();
    }

    onClick() {
        const {open, step, toggleOpen}  = this.props;
        if (!open) {
            toggleOpen(step);
        }
    }

    executeQuery() {
        this.setState({hasExecuted: true});
    }

    hideMenus() {
        inputBlurEvent.emit();
        this.forceUpdate();
    }

    handleColumnSelect(attribute, hadColumn) {
        if (attribute.datasetId === this.props.step.id) {
            this.setDatasetAttribute(attribute);
            if (
                !hadColumn &&
                (
                    FEATURE_FLAGS.QUERY_BUILDER_TYPE === QUERY_BUILDER_TYPES.SINGLE_INPUT ||
                    attribute.type === ATTRIBUTES.FILTER
                )
            ) {
                nextMenuEvent.emit();
            } else {
                inputBlurEvent.emit();
            }
        }

        this.setState({hasExecuted: false});
    }

    handleAggregationSelect(attribute, hadAgg) {
        if (attribute.datasetId === this.props.step.id) {
            this.setDatasetAttribute(attribute);
            if (!hadAgg) {
                nextMenuEvent.emit();
            } else {
                inputBlurEvent.emit();
            }
        }

        this.setState({hasExecuted: false});
    }

    setFilterOperator(attribute, hadOperator) {
        if (attribute.datasetId === this.props.step.id) {
            this.setDatasetAttribute(attribute);
            if (!hadOperator) {
                nextMenuEvent.emit();
            } else {
                inputBlurEvent.emit();
            }
        }
    }

    setFilterValue(attribute, hadValues) {
        if (attribute.datasetId === this.props.step.id) {
            this.setDatasetAttribute(attribute);
            if (!hadValues) {
                nextMenuEvent.emit();
            } else {
                inputBlurEvent.emit();
            }
        }

        this.setState({hasExecuted: false});
    }

    setFilterLogicalOperator(value) {
        const type = ATTRIBUTES.FILTER;
        const attribute = createDatasetAttribute(type, undefined, queryBuilderState.attribute);
        attribute.setLogicalOperator(value);
        this.setDatasetAttribute(attribute);
        nextMenuEvent.emit();
    }

    changeDatasource(datasourceName) {
        const dataset = new DatasetModel(this.props.step);
        dataset.changeDatasource(datasourceName);
        this.props.updateStep(dataset);
        this.setState({datasource: DATASOURCES[datasourceName]});
    }

    setDatasetAttribute(attribute) {
        const dataset = new DatasetModel(this.props.step);
        dataset.addOrUpdateAttribute(attribute);
        this.props.updateStep(dataset);
    }

    removeDatasetAttribute(attribute) {
        const dataset = new DatasetModel(this.props.step);
        dataset.removeAttribute(attribute);
        this.props.updateStep(dataset);
        this.setState({hasExecuted: false});
    }

    renderMenu() {
        const {datasource} = this.state;
        switch (queryBuilderState.menu) {
            case MENUS.COLUMN:
            case MENUS.COLUMN_AND_AGG:
                return <ColumnSelector datasource={datasource} />;
            case MENUS.AGGREGATION:
            case MENUS.COMBINED_AGG:
                return queryBuilderState.attribute.column ? (
                    <AggregationSelector
                        column={queryBuilderState.attribute.column}
                        showGroupBy={queryBuilderState.menu === MENUS.COMBINED_AGG}
                    />
                ) : null;
            case MENUS.FILTER_OPERATOR:
                return queryBuilderState.attribute.column ?
                    <FilterOperatorSelector /> :
                    null;
            case MENUS.FILTER_VALUE:
                return <FilterValuesSelector column={queryBuilderState.attribute.column} />;
            case MENUS.FILTER_LOGICAL_OPERATOR:
                return <FilterLogicalSelector onSelect={this.setFilterLogicalOperator} />;
            default:
                return null;
        }
    }

    renderQueryBuilder() {
        const {step: dataset, open} = this.props;
        const {datasource, queryBuilderType} = this.state;
        switch (queryBuilderType) {
            case QUERY_BUILDER_TYPES.COLUMN_FIRST:
                return (
                    <>
                        <div className="form-block-2 w-form">
                            <form
                                id="email-form-2"
                                name="email-form-2"
                                data-name="Email Form 2"
                                className="w-clearfix"
                            >
                                <label className="label dataset-label-columns">COLUMNS</label>
                                <QueryBuilderColumnFirst
                                    datasource={datasource}
                                    dataset={dataset}
                                    onClick={this.onClick}
                                    onChange={this.setDatasetAttribute.bind(this)}
                                    onRemove={this.removeDatasetAttribute.bind(this)}
                                    open={open}
                                />
                            </form>
                        </div>
                    </>
                );
            case QUERY_BUILDER_TYPES.SINGLE_INPUT:
                return (
                    <div className="form-block-2 w-form">
                        <form
                            id="email-form-2"
                            name="email-form-2"
                            data-name="Email Form 2"
                            className="w-clearfix"
                        >
                            <label className="label">I want to see...</label>
                            <QueryBuilderSingleInput
                                datasource={datasource}
                                dataset={dataset}
                                onClick={this.onClick}
                                onRemove={this.removeDatasetAttribute.bind(this)}
                            />
                        </form>
                    </div>
                );
            case QUERY_BUILDER_TYPES.HYBRID:
                return (
                    <div className="form-block-2 w-form">
                        <form
                            id="email-form-2"
                            name="email-form-2"
                            data-name="Email Form 2"
                            className="w-clearfix"
                        >
                            <label className="label dataset-label-columns">COLUMNS</label>
                            <QueryBuilderHybrid
                                datasource={datasource}
                                dataset={dataset}
                                onClick={this.onClick}
                                onRemove={this.removeDatasetAttribute.bind(this)}
                            />
                        </form>
                    </div>
                );
            default:
                return null;
        }
    }

    renderQueryResult() {
        const {queryBuilderType, hasExecuted} = this.state;
        const {step: dataset, adjacentToSelected, showTable} = this.props;
        switch (queryBuilderType) {
            case QUERY_BUILDER_TYPES.COLUMN_FIRST:
                return (
                    <ColumnFirstQueryResult
                        dataset={dataset}
                        showTable={showTable}
                        onClick={this.onClick}
                        hasExecuted={hasExecuted}
                        onExecute={this.executeQuery}
                    />
                );
            default:
                return (
                    <SingleInputQueryResult
                        dataset={dataset}
                        showTable={showTable}
                        adjacentToSelected={adjacentToSelected}
                        onClick={this.onClick}
                    />
                );
        }
    }

    render() {
        const {step: dataset, showTable, open, transitionDuration} = this.props;
        const {hasExecuted} = this.state;
        return (
            <div className={`pipeline-step pipeline-step-dataset ${open ? 'pipeline-step-open' : 'pipeline-step-closed'}`}>
                <div className="pipeline-step-header query-result-header">
                    <div className="pipeline-step-header-text">
                        <img src="images/transformations-dataset.svg" alt="Dataset"/>
                        <span className="pipeline-step-text">
                            Dataset 1
                            {!open && <span className="pipeline-step-text-subtitle">{Object.values(DATASOURCES)[0].label}</span>}
                        </span>
                    </div>
                    <div className="pipeline-step-header-actions">
                        {open ? (
                            <>
                                <label className="label label-datasource">Data Source</label>
                                <SourceSelector
                                    datasources={Object.values(DATASOURCES)}
                                    selectedDatasource={dataset.datasource}
                                    onChange={this.changeDatasource.bind(this)}
                                />
                            </>
                        ) :
                            <button
                                className="open-toggle"
                                onClick={this.onClick}
                            >
                                <img
                                    src="images/down-chevron.svg"
                                    alt=""
                                    width="20"
                                    height="20"
                                />
                            </button>
                        }

                    </div>
                </div>

                {open && dataset.columns.length > 0 && hasExecuted && FEATURE_FLAGS.TABLE_MENU_SIDE ? <TableMenu step={dataset} /> : null}

                <Transition
                    in={showTable}
                    timeout={transitionDuration}
                >
                    {(state) => (
                        <>
                            <div className="pipeline-step-body">
                                {open ?
                                    this.renderQueryBuilder() :
                                    <div className="pipeline-dataset-columns">
                                        {dataset.columns.map(attribute =>
                                            <ColumnName key={attribute.id} column={attribute.column} separator={' '} />
                                        )}
                                    </div>
                                }
                            </div>
                            {this.renderMenu()}
                            <div
                                style={{
                                    ...getDefaultResultsTableStyles(transitionDuration),
                                    ...getResultsTableStyles(hasExecuted ? 490 : 250)[state]
                                }}
                            >
                                {(open && (dataset.measures.length || dataset.dimensions.length)) ?
                                    <>
                                        <label className="label dataset-label-preview">Preview</label>
                                        {this.renderQueryResult()}
                                    </> :
                                    null
                                }
                            </div>
                        </>
                    )}
                </Transition>
            </div>
        );
    }
}

DatasetPanel.propTypes = {
    step: PropTypes.instanceOf(DatasetModel).isRequired,
};

export default withFeatureFlag('STEP_TRANSITION_DURATION', 'transitionDuration', DatasetPanel)
